import React, { useMemo, useState } from 'react';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/utils/analytics';
import {
  EVENT_WEBSITE_UNAVAILABLE_MESSAGE,
  EXTERNAL_LINK_DISCLAIMER_VERSION,
  getEventWebsiteControl,
} from '@/utils/externalEventLinks';
import { TERMS_OF_SERVICE_PATH } from '@/data/governedCompliance';

export const EventWebsiteButton = ({ event, className = '', buttonTestId = 'visit-event-website-button' }) => {
  const [open, setOpen] = useState(false);
  const control = useMemo(() => getEventWebsiteControl(event), [event]);

  const handleContinue = () => {
    if (!control.publicClickable || !control.url) {
      return;
    }

    trackEvent('external_event_link_confirmed', {
      event_id: event?.id,
      destination_hostname: control.hostname,
      link_review_status: control.status,
      disclaimer_version: EXTERNAL_LINK_DISCLAIMER_VERSION,
    });

    const anchor = document.createElement('a');
    anchor.href = control.url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer external';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setOpen(false);
  };

  const modalMessage = control.publicClickable
    ? 'You are leaving Jigsaw Events Hub and opening a third-party website listed in our event data as relevant to this event. Jigsaw Conferences Ltd does not operate or control the destination website. Event information and third-party websites can change. Before purchasing tickets, sharing personal information or making travel or accommodation arrangements, please check the website address and confirm the event name, organiser, dates and venue. Any use of the external website, including purchases or the submission of personal information, is subject to that website’s own terms and privacy policy. To the fullest extent permitted by law, Jigsaw Conferences Ltd is not responsible for the content, security, availability or transactions of third-party websites. Nothing in this notice excludes or limits any liability that cannot lawfully be excluded or limited.'
    : EVENT_WEBSITE_UNAVAILABLE_MESSAGE;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={className} data-testid={buttonTestId}>
          <ExternalLink className="mr-2 h-5 w-5" />
          Visit Event Website
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-[560px] rounded-[24px] border-slate-200 bg-white p-0 shadow-[0_24px_80px_rgba(15,23,42,0.18)]"
        data-testid="external-website-dialog"
      >
        <div className="border-b border-slate-100 px-6 py-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500" data-testid="external-website-dialog-label">
            EXTERNAL WEBSITE
          </p>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-2xl text-slate-900" data-testid="external-website-dialog-title">
              <span className="rounded-full bg-orange-100 p-2 text-orange-600">
                <ShieldCheck className="h-5 w-5" />
              </span>
              Before you visit this website
            </DialogTitle>
            <DialogDescription className="pt-3 text-sm leading-6 text-slate-500">
              Review the destination website details before leaving Jigsaw Events Hub.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-4 px-6 py-5 text-[15px] leading-7 text-slate-600">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="external-website-destination">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Destination</p>
            <p className="mt-1 text-base font-semibold text-slate-900">{control.hostname || 'Unavailable'}</p>
          </div>
          <p data-testid="external-website-dialog-copy">{modalMessage}</p>
          <a href={TERMS_OF_SERVICE_PATH} className="inline-flex text-sm font-medium text-orange-700 hover:text-orange-800" data-testid="external-website-terms-link">
            Terms of Service
          </a>
        </div>

        <DialogFooter className="border-t border-slate-100 bg-slate-50 px-6 py-5 sm:justify-between">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="external-website-stay-button">
            Stay on Jigsaw
          </Button>

          <div className="flex flex-col items-end gap-2">
            <Button
              className="bg-orange-600 text-white hover:bg-orange-700"
              onClick={handleContinue}
              disabled={!control.publicClickable}
              data-testid="external-website-continue-button"
            >
              Continue to website
            </Button>
            {control.publicClickable && (
              <p className="text-xs text-slate-500" data-testid="external-website-open-note">Opens in a new tab</p>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};